import {
  computed,
  ref,
  reactive,
  readonly,
  provide
} from "vue";
import { refreshNuxtData } from "nuxt/app";
import { INJECT_MUTATED_FIELDS_MAP } from "./symbols.js";
import onBlokkliEvent from "./composables/onBlokkliEvent.js";
import { falsy, getFieldKey } from "#blokkli/helpers";
import { eventBus, emitMessage } from "#blokkli/helpers/eventBus";
import { nextTick } from "#imports";
export default async function(adapter, context, $t) {
  const stateLoaded = ref(false);
  const stateLoadError = ref(false);
  const owner = ref(null);
  const refreshKey = ref("");
  const mutatedFields = ref([]);
  const mutatedFieldsMap = reactive(
    {}
  );
  const mutations = ref([]);
  const violations = ref([]);
  const mutatedEntity = ref(null);
  const currentMutationIndex = ref(-1);
  const isLoading = ref(false);
  const entity = ref({
    label: "",
    status: false,
    bundleLabel: ""
  });
  let fieldBlockCount = {};
  const blockBundleCount = ref({});
  const fieldListItemMap = {};
  function getFieldListItem(uuid) {
    const fieldKey = fieldListItemMap[uuid];
    if (!fieldKey) {
      return;
    }
    const field = mutatedFieldsMap[fieldKey];
    if (!field) {
      return;
    }
    return field.list.find((v) => v.uuid === uuid);
  }
  function getFieldListForBlock(uuid) {
    const fieldKey = fieldListItemMap[uuid];
    if (!fieldKey) {
      return;
    }
    return mutatedFieldsMap[fieldKey];
  }
  const mutatedOptions = reactive({});
  const translation = ref({
    isTranslatable: false,
    sourceLanguage: "",
    availableLanguages: [],
    translations: []
  });
  function setContext(context2) {
    const options = context2?.mutatedState?.mutatedOptions || {};
    const optionKeys = Object.keys(options);
    for (let i = 0; i < optionKeys.length; i++) {
      const key = optionKeys[i];
      const newOptions = options[key];
      const existing = mutatedOptions[key];
      if (!existing || JSON.stringify(existing) !== JSON.stringify(newOptions)) {
        mutatedOptions[key] = newOptions;
      }
    }
    mutations.value = context2?.mutations || [];
    violations.value = context2?.mutatedState?.violations || [];
    const currentIndex = context2?.currentIndex;
    currentMutationIndex.value = currentIndex === void 0 ? -1 : currentIndex;
    owner.value = {
      name: context2?.ownerName,
      currentUserIsOwner: !!context2?.currentUserIsOwner
    };
    entity.value.label = context2?.entity?.label;
    entity.value.status = context2?.entity?.status;
    entity.value.bundleLabel = context2?.entity?.bundleLabel || "";
    translation.value.isTranslatable = !!context2?.translationState?.isTranslatable;
    translation.value.translations = context2?.translationState?.translations?.filter(falsy) || [];
    translation.value.sourceLanguage = context2?.translationState?.sourceLanguage || "";
    translation.value.availableLanguages = context2?.translationState?.availableLanguages || [];
    const newMutatedFields = (context2?.mutatedState?.fields || []).map(
      (field) => {
        return {
          ...field,
          list: field.list.filter(falsy)
        };
      }
    );
    mutatedFields.value = newMutatedFields;
    mutatedEntity.value = context2?.mutatedEntity;
    const visitedFieldKeys = [];
    const newBlockBundleCount = {};
    fieldBlockCount = {};
    for (let i = 0; i < newMutatedFields.length; i++) {
      const field = newMutatedFields[i];
      const key = getFieldKey(field.entityUuid, field.name);
      visitedFieldKeys.push(key);
      const existing = mutatedFieldsMap[key];
      if (!existing || existing.list.length !== field.list.length || JSON.stringify(existing.list) !== JSON.stringify(field.list)) {
        mutatedFieldsMap[key] = field;
      }
      for (let j = 0; j < field.list.length; j++) {
        const item = field.list[j];
        if (!newBlockBundleCount[item.bundle]) {
          newBlockBundleCount[item.bundle] = 0;
        }
        newBlockBundleCount[item.bundle]++;
        fieldListItemMap[item.uuid] = key;
      }
    }
    blockBundleCount.value = newBlockBundleCount;
    const existingKeys = Object.keys(mutatedFieldsMap);
    for (let i = 0; i < existingKeys.length; i++) {
      const key = existingKeys[i];
      if (!visitedFieldKeys.includes(key)) {
        mutatedFieldsMap[key] = void 0;
      }
    }
    eventBus.emit("updateMutatedFields", { fields: newMutatedFields });
    nextTick(() => {
      refreshKey.value = Date.now().toString();
      eventBus.emit("state:reloaded");
    });
  }
  function getMutatedField(entityUuid, fieldName) {
    const key = getFieldKey(entityUuid, fieldName);
    return mutatedFieldsMap[key];
  }
  function getBlockBundleCount(bundle) {
    return blockBundleCount.value[bundle] || 0;
  }
  function getFieldBlockCount(key) {
    if (fieldBlockCount[key] !== void 0) {
      return fieldBlockCount[key];
    }
    const count = mutatedFieldsMap[key]?.list.length || 0;
    fieldBlockCount[key] = count;
    return count;
  }
  function lockBody() {
    document.body.classList.add("bk-body-loading");
    isLoading.value = true;
  }
  function unlockBody() {
    document.body.classList.remove("bk-body-loading");
    isLoading.value = false;
  }
  const mutateWithLoadingState = async (callback, errorMessage, successMessage) => {
    if (!callback) {
      return true;
    }
    lockBody();
    try {
      const promise = callback();
      const result = await promise;
      if (!result) {
        throw new Error("Unexpected error");
      }
      unlockBody();
      if (result.state) {
        setContext(adapter.mapState(result.state));
      }
      if (!result.success) {
        const errorMessage2 = result.errors?.join("\n") || $t("unexpectedMutationError", "An unexpected error happened.");
        throw new Error(errorMessage2);
      }
      if (successMessage) {
        emitMessage(successMessage);
      }
      return true;
    } catch (e) {
      emitMessage(
        errorMessage || $t("unexpectedMutationError", "An unexpected error happened."),
        "error",
        e
      );
    }
    unlockBody();
    return false;
  };
  async function loadState() {
    try {
      const state = await adapter.loadState();
      if (!state) {
        throw new Error("Missing state.");
      }
      setContext(adapter.mapState(state));
      stateLoadError.value = false;
      stateLoaded.value = true;
    } catch {
      stateLoadError.value = true;
      stateLoaded.value = false;
    }
  }
  const canEdit = computed(
    () => stateLoaded.value && !!owner.value?.currentUserIsOwner && !stateLoadError.value
  );
  const isTranslation = computed(
    () => context.value.language !== translation.value.sourceLanguage && translation.value.isTranslatable
  );
  const editMode = computed(() => {
    if (!canEdit.value) {
      return "readonly";
    }
    if (isTranslation.value) {
      return "translating";
    }
    return "editing";
  });
  onBlokkliEvent("reloadState", async () => {
    await loadState();
  });
  onBlokkliEvent("reloadEntity", async (cb) => {
    await refreshNuxtData();
    await loadState();
    if (cb) {
      cb();
    }
  });
  provide(INJECT_MUTATED_FIELDS_MAP, mutatedFieldsMap);
  await loadState();
  const stateAvailable = computed(
    () => stateLoaded.value && !stateLoadError.value
  );
  return {
    stateAvailable,
    refreshKey,
    owner: readonly(owner),
    mutatedFields,
    entity,
    mutatedOptions,
    translation,
    mutations,
    violations,
    currentMutationIndex,
    mutateWithLoadingState,
    editMode,
    canEdit,
    isLoading: readonly(isLoading),
    mutatedEntity,
    getFieldBlockCount,
    getBlockBundleCount,
    getFieldListItem,
    getMutatedField,
    getFieldListForBlock
  };
}
